import { useState, useEffect, useCallback } from 'react';
import type { TourStep } from '@/components/ui/product-tour';

const TOUR_COMPLETED_KEY = 'audit-trail-tour-completed';

export function useAuditTrailTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_COMPLETED_KEY);
    setHasCompletedTour(completed === 'true');
  }, []);

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    setIsTourOpen(false);
    setHasCompletedTour(true);
    localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="audit-header"]',
      title: 'Welcome to Audit Trail',
      content: 'The Audit Trail provides a complete history of all system changes and user actions. Track who did what, when, and see detailed change information.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="search-input"]',
      title: 'Search Audit Logs',
      content: 'Quickly find specific audit records by searching across all fields including user names, actions, asset codes, and change descriptions.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="audit-table"]',
      title: 'Audit Log Records',
      content: 'View all audit records in this table. Each row shows details about a specific action including timestamp, user, action type, asset affected, and what changed.',
      placement: 'top',
    },
    {
      target: '[data-tour="select-checkbox-input"]',
      title: 'Select Records',
      content: 'Select individual audit records or use this checkbox to select all records on the current page for bulk actions like exporting.',
      placement: 'right',
    },
    {
      target: '[data-tour="action-type-badge"]',
      title: 'Action Types',
      content: 'Each audit record is color-coded by action type (Create, Update, Delete, etc.) for easy visual identification.',
      placement: 'left',
    },
    {
      target: '[data-tour="pagination"]',
      title: 'Navigate Pages',
      content: 'Use these controls to navigate through pages of audit records. You can jump to first/last page or move one page at a time.',
      placement: 'top',
    },
  ];

  return {
    tourSteps,
    isTourOpen,
    hasCompletedTour,
    startTour,
    closeTour,
    completeTour,
  };
}
